// Translation layer: PixelRuntime SDKEvent → Shopify analytics call.
//
// Uses hydrogen-react's exported helpers so the on-the-wire payload stays
// in sync with whatever Shopify's stack expects:
//   - getClientBrowserParameters() → uniqueToken / visitToken / nav fields
//   - sendShopifyAnalytics()       → builds the schema-wrapped events and
//                                    POSTs to monorail
//
// We don't reimplement the schema or transport here — that's the whole
// point of this rewrite. We just shape the input.

// Pull from the React-free subpaths so the bundle stays tiny and the runtime
// doesn't need React. These submodules only depend on `cart-constants`,
// `tracking-utils`, and the analytics schemas — all pure JS.
import {
	AnalyticsEventName,
	ShopifySalesChannel,
} from '@shopify/hydrogen-react/analytics-constants';
import {
	getClientBrowserParameters,
	sendShopifyAnalytics,
} from '@shopify/hydrogen-react/analytics';
import type {
	ShopifyAnalyticsProduct,
	ShopifyPageViewPayload,
} from '@shopify/hydrogen-react/analytics-types';
import { mirrorAddToCart } from './cartMirror';
import type { TrackingTokens } from './tracking';
import type { SDKEvent, ShopifyPixelConfig } from './types';

export interface ResolvedConfig
	extends Required<
		Pick<
			ShopifyPixelConfig,
			'shopId' | 'currency' | 'acceptedLanguage' | 'shopifySalesChannel' | 'storefrontId' | 'hasUserConsent'
		>
	> {
	domain: string;
	merchantName: string;
	shopMonorailDomain: string;
	gdprEnforced: boolean;
	ccpaEnforced: boolean;
	cartMirror: boolean;
}

export function resolveConfig(raw: ShopifyPixelConfig): ResolvedConfig {
	if (!raw || !raw.shopId) {
		throw new Error('[shopify-pixel] config.shopId is required');
	}
	return {
		shopId: String(raw.shopId),
		domain: raw.domain || '',
		merchantName: raw.merchantName || '',
		currency: (raw.currency || 'INR').toUpperCase(),
		acceptedLanguage: raw.acceptedLanguage || 'en',
		shopifySalesChannel: raw.shopifySalesChannel || 'headless',
		storefrontId: raw.storefrontId || '0',
		shopMonorailDomain: raw.shopMonorailDomain || '',
		hasUserConsent: raw.hasUserConsent !== false,
		gdprEnforced: !!raw.gdprEnforced,
		ccpaEnforced: !!raw.ccpaEnforced,
		cartMirror: raw.cartMirror === true,
	};
}

function toShopGid(shopId: string): string {
	return /^gid:\/\//.test(shopId) ? shopId : `gid://shopify/Shop/${shopId}`;
}

function toProductGid(id: unknown): string {
	if (id == null) return '';
	const s = String(id);
	return /^gid:\/\//.test(s) ? s : `gid://shopify/Product/${s}`;
}

function toVariantGid(id: unknown): string {
	if (id == null) return '';
	const s = String(id);
	return /^gid:\/\//.test(s) ? s : `gid://shopify/ProductVariant/${s}`;
}

/**
 * Build the base payload every monorail event shares. Equivalent to
 * `shopify-adapter.ts#createBasePayload()` from packages/analytics.
 */
export function buildBasePayload(cfg: ResolvedConfig, tokens: TrackingTokens): Record<string, unknown> {
	return {
		...getClientBrowserParameters(),
		uniqueToken: tokens.uniqueToken,
		visitToken: tokens.visitToken,
		hasUserConsent: cfg.hasUserConsent,
		analyticsAllowed: cfg.hasUserConsent,
		marketingAllowed: cfg.hasUserConsent,
		// Matches Shopify's own default (allowed unless the visitor opted out).
		saleOfDataAllowed: cfg.hasUserConsent,
		gdprEnforced: cfg.gdprEnforced,
		ccpaEnforced: cfg.ccpaEnforced,
		storefrontId: cfg.storefrontId,
		shopifySalesChannel:
			cfg.shopifySalesChannel === 'hydrogen'
				? ShopifySalesChannel.hydrogen
				: ShopifySalesChannel.headless,
		shopId: toShopGid(cfg.shopId),
		currency: cfg.currency,
		acceptedLanguage: cfg.acceptedLanguage,
	};
}

interface ProductIds {
	productGid: string;
	variantGid: string;
	sku?: string;
}

/**
 * Work out what a Meta-flavoured content id actually is. The storefront's
 * `productIdentifier` setting (chosen for Meta catalog matching) decides
 * whether `contents[].id` / `content_ids[]` carry product ids, variant ids
 * or SKUs, and only `content_type` tells us which:
 *
 *   - `product_group` → product id
 *   - `product`       → variant id (numeric / ProductVariant GID) or SKU
 *
 * Explicit `product_id` / `variant_id` fields win when the runtime sends them.
 * We never label a variant id as a product — Shopify can't attribute an
 * event to a product GID that doesn't exist.
 */
function resolveProductIds(
	item: Record<string, any> | undefined,
	rawId: unknown,
	contentType: unknown,
): ProductIds {
	const explicitProduct = item?.product_id;
	const explicitVariant = item?.variant_id;
	const sku = item?.sku ? String(item.sku) : undefined;

	if (explicitProduct != null || explicitVariant != null) {
		// Fill whichever side is missing from `id`, per content_type.
		const idIsVariant = contentType === 'product';
		const productSource = explicitProduct ?? (idIsVariant ? undefined : rawId);
		const variantSource =
			explicitVariant ?? (idIsVariant && /^\d+$/.test(String(rawId ?? '')) ? rawId : undefined);
		return {
			productGid: productSource != null && productSource !== '' ? toProductGid(productSource) : '',
			variantGid: variantSource != null && variantSource !== '' ? toVariantGid(variantSource) : '',
			sku,
		};
	}

	if (rawId == null || rawId === '') return { productGid: '', variantGid: '', sku };
	const id = String(rawId);

	if (/^gid:\/\/shopify\/ProductVariant\//.test(id)) return { productGid: '', variantGid: id, sku };
	if (/^gid:\/\/shopify\/Product\//.test(id)) return { productGid: id, variantGid: '', sku };

	if (contentType === 'product') {
		// Variant-level ids. Numeric → variant id; anything else is a SKU.
		return /^\d+$/.test(id)
			? { productGid: '', variantGid: toVariantGid(id), sku }
			: { productGid: '', variantGid: '', sku: sku ?? id };
	}

	// `product_group` or no content_type (legacy runtimes) → product id.
	return { productGid: toProductGid(id), variantGid: '', sku };
}

/**
 * Map runtime `properties` (which use Meta-flavoured `contents[]` /
 * `content_ids`) into Shopify's product shape.
 */
export function productsFromEvent(event: SDKEvent): ShopifyAnalyticsProduct[] {
	const props = (event.properties ?? {}) as Record<string, any>;
	const out: ShopifyAnalyticsProduct[] = [];

	if (Array.isArray(props.contents) && props.contents.length) {
		for (const c of props.contents) {
			out.push({
				...resolveProductIds(c, c?.id, props.content_type),
				quantity: Number(c?.quantity ?? 1),
				price: c?.item_price != null ? String(c.item_price) : '0',
				name: c?.name || props.content_name || '',
				brand: c?.brand || '',
				category: c?.category || props.content_category,
			} as ShopifyAnalyticsProduct);
		}
		return out;
	}

	if (Array.isArray(props.content_ids) && props.content_ids.length) {
		out.push({
			...resolveProductIds(undefined, props.content_ids[0], props.content_type),
			quantity: 1,
			price: props.value != null ? String(props.value) : '0',
			name: props.content_name || '',
			brand: '',
			category: props.content_category,
		} as ShopifyAnalyticsProduct);
	}
	return out;
}

function totalValueOf(event: SDKEvent): number | undefined {
	const v = (event.properties as Record<string, unknown>)?.value;
	return typeof v === 'number' ? v : undefined;
}

interface DispatchOptions {
	pageType?: 'collection' | 'product' | 'search' | 'checkout';
}

/**
 * Hand the assembled payload to hydrogen-react's `sendShopifyAnalytics`.
 * The shop domain (when configured) routes monorail through the
 * customer's *.myshopify.com domain; otherwise hydrogen's default
 * `https://monorail-edge.shopifysvc.com/...` is used.
 */
function dispatch(
	cfg: ResolvedConfig,
	eventName: keyof typeof AnalyticsEventName,
	payload: Record<string, unknown>,
	opts: DispatchOptions = {},
): void {
	if (!cfg.hasUserConsent) return;

	const enriched = {
		...payload,
		...(opts.pageType ? { pageType: opts.pageType } : {}),
	} as unknown as ShopifyPageViewPayload;

	// hydrogen-react's helpers return Promises; we fire-and-forget but log
	// on rejection so pipeline issues don't disappear silently.
	sendShopifyAnalytics(
		{ eventName, payload: enriched },
		cfg.shopMonorailDomain || undefined,
	)?.catch((err) => {
		// eslint-disable-next-line no-console
		console.warn('[shopify-pixel] dispatch failed', err);
	});
}

// ─── Public per-event handlers ────────────────────────────────────────────

export function sendPageView(cfg: ResolvedConfig, tokens: TrackingTokens, _event: SDKEvent): void {
	dispatch(cfg, 'PAGE_VIEW', buildBasePayload(cfg, tokens));
}

export function sendCheckout(cfg: ResolvedConfig, tokens: TrackingTokens, _event: SDKEvent): void {
	dispatch(cfg, 'PAGE_VIEW', buildBasePayload(cfg, tokens), { pageType: 'checkout' });
}

export function sendProductView(cfg: ResolvedConfig, tokens: TrackingTokens, event: SDKEvent): void {
	dispatch(cfg, 'PRODUCT_VIEW', {
		...buildBasePayload(cfg, tokens),
		products: productsFromEvent(event),
		totalValue: totalValueOf(event),
	});
}

// ShopKit cart storage writes `merchant_<storefront merchant name>_<field>`.
// The storefront's merchant name (e.g. `bblunt-2`) is not the GoKwik
// merchantName we get from /sdk-config (e.g. `BBlunt-OS`), so we can't
// build the key — find it by shape instead.
const CART_KEY_PATTERN = /^merchant_.+_cart(Token|Id)$/;

/**
 * Read the storefront's cart id/token and return it as a Cart GID —
 * hydrogen-react only extracts `cart_token` from `gid://…` values, so a
 * bare token would be dropped as null.
 */
export function readCartGid(merchantName: string): string {
	let token = '';
	try {
		const preferred = merchantName
			? [`merchant_${merchantName}_cartToken`, `merchant_${merchantName}_cartId`]
			: [];
		for (const key of preferred) {
			token = localStorage.getItem(key) || '';
			if (token) break;
		}
		for (let i = 0; !token && i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && CART_KEY_PATTERN.test(key)) token = localStorage.getItem(key) || '';
		}
	} catch {
		// localStorage unavailable (private mode etc.) — ship without cartId
		return '';
	}
	if (!token) return '';
	return /^gid:\/\//.test(token) ? token : `gid://shopify/Cart/${token}`;
}

/**
 * When the merchant has set up cart mirroring, first add the item to the
 * visitor's analytics-only Shopify cart (see cartMirror.ts) — Shopify only
 * counts add-to-carts that point at a Shopify cart. Otherwise, or if the
 * mirror call fails, send the event with the storefront's own cart id as
 * before (Shopify accepts it but won't count it).
 */
export async function sendAddToCart(
	cfg: ResolvedConfig,
	tokens: TrackingTokens,
	event: SDKEvent,
): Promise<void> {
	const products = productsFromEvent(event);
	let cartId = '';

	if (cfg.cartMirror) {
		// Only variants can go into a Shopify cart.
		const lines = products
			.filter((p) => p.variantGid)
			.map((p) => ({ variantId: p.variantGid as string, quantity: Number(p.quantity) || 1 }));
		const mirror = await mirrorAddToCart(cfg.shopId, tokens, lines);
		if (mirror) {
			cartId = mirror.cartId;
			for (const p of products) {
				const productGid = p.variantGid && mirror.productIdByVariant.get(p.variantGid);
				if (!p.productGid && productGid) p.productGid = productGid;
			}
		}
	}

	if (!cartId) cartId = readCartGid(cfg.merchantName);

	dispatch(cfg, 'ADD_TO_CART', {
		...buildBasePayload(cfg, tokens),
		cartId,
		products,
		totalValue: totalValueOf(event),
	});
}
